/**
 * Custom A4 invoice designer — shared model + renderer.
 *
 * The A4 sibling of `receiptDesign.ts`. Same contract: the admin designer's
 * live preview and every print path import the renderer from here, so what
 * is designed is what prints. The difference is the page: a thermal receipt
 * is a flow of blocks down a narrow band, an A4 invoice is a SHEET — company
 * masthead top-left and top-right, boxed invoice/date fields, a customer
 * block, an items table, totals, a signature strip. So a design here is a
 * page plus POSITIONED elements: each has x / y / width / height in
 * millimetres, can be moved and resized, and is absolutely placed on the
 * 210 × 297 sheet. The one exception is the items table, whose height depends
 * on the order; elements that must sit BELOW it (a "received in good
 * condition" line, the signatures) anchor to its bottom edge instead of the
 * page, and move with it.
 *
 * The renderer is pure (no DOM / no React) and emits a full standalone HTML
 * document. It consumes an `A4RenderModel` that carries NUMBERS for money —
 * unlike the receipt model — because a Gulf invoice prints dinars and fils in
 * separate columns, and only the renderer knows which column wants which part.
 */

import type { ReceiptPrintOptions } from './print';

// ── Design model ───────────────────────────────────────────────────────────

export type A4Align = 'left' | 'center' | 'right';
export type A4LineStyle = 'solid' | 'dashed' | 'dotted' | 'double';
export type A4Anchor = 'page' | 'after-items';

export interface A4TextStyle {
  fontFamily?: string;
  fontSizePt?: number;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  align?: A4Align;
  /** Vertical alignment inside the element box. */
  valign?: 'top' | 'middle' | 'bottom';
  color?: string;
  background?: string;
  lineHeight?: number;
  direction?: 'ltr' | 'rtl';
  uppercase?: boolean;
}

/** Box decoration around an element (or around a field's value box). */
export interface A4Frame {
  borderWidthPx?: number;
  borderColor?: string;
  borderStyle?: A4LineStyle;
  radiusMm?: number;
  paddingMm?: number;
  background?: string;
}

interface ElementBase {
  id: string;
  /** Left edge, millimetres from the page's left edge. */
  x: number;
  /** Top edge, millimetres from the page top (or from the items table's bottom when anchored). */
  y: number;
  w: number;
  h: number;
  /**
   * 'page' (default) places the element at (x, y) on the sheet. 'after-items'
   * places it (x, y) millimetres below the bottom edge of the items table, so
   * a footer designed under a three-line sample stays under a thirty-line
   * order.
   */
  anchor?: A4Anchor;
  style?: A4TextStyle;
  frame?: A4Frame;
  /** Skip the whole element when this token resolves to an empty value. */
  hideIfEmpty?: string;
  /** Free label shown in the designer's element list only. */
  name?: string;
  /** 0–1. Unset (or 1) draws the element fully opaque, as before. */
  opacity?: number;
  /**
   * Turn the element's content inside its box, clockwise, in degrees. 90 and
   * 270 are for VERTICAL text down a page margin ("goods sold are not
   * returned"): draw the box tall and narrow and the text runs along it.
   */
  rotate?: 0 | 90 | 180 | 270;
}

/** Free text with {{token}} placeholders; newlines are kept. */
export interface A4TextElement extends ElementBase {
  type: 'text';
  text: string;
}

/**
 * A labelled value — "Invoice no. [7042]". The label can sit before the value,
 * above it, or on BOTH sides (English at the start, Arabic at the end — the
 * bilingual row every Gulf form uses).
 */
export interface A4FieldElement extends ElementBase {
  type: 'field';
  token: string;
  labelEn?: string;
  labelAr?: string;
  labelLayout?: 'inline' | 'stacked' | 'both-ends' | 'none';
  /** Fixed width of the start-side label column, mm (inline / both-ends). */
  labelWidthMm?: number;
  labelStyle?: A4TextStyle;
  valueStyle?: A4TextStyle;
  /** Box drawn around the value only (the "[7042]" box on the reference). */
  valueFrame?: A4Frame;
  /**
   * Let the value run onto more lines instead of being clipped with an
   * ellipsis. Off by default, because a one-line cell is what keeps a boxed
   * grid's rows aligned; turn it on for the fields that carry a sentence — a
   * note, a long address — and give the element the height to hold it.
   */
  wrap?: boolean;
  /**
   * Box drawn around the LABEL, turning it into a cell of its own — the shaded
   * caption column of the header grids these ERP forms use ("الفرع │ فرع الرى").
   * A framed label stretches to the element's full height; an unframed one
   * stays a plain inline caption, exactly as before.
   */
  labelFrame?: A4Frame;
}

export interface A4LogoElement extends ElementBase {
  type: 'logo';
  /** Which uploaded artwork: the receipt logo (default) or the menu logo. */
  source?: 'receipt' | 'menu';
  fit?: 'contain' | 'cover';
}

export interface A4ImageElement extends ElementBase {
  type: 'image';
  dataUrl: string;
  fit?: 'contain' | 'cover';
}

export interface A4LineElement extends ElementBase {
  type: 'line';
  orientation?: 'h' | 'v';
  thicknessPx?: number;
  lineStyle?: A4LineStyle;
  color?: string;
}

/** An empty box — a border and/or a fill, for grouping other elements. */
export interface A4RectElement extends ElementBase {
  type: 'rect';
}

/** The ZATCA QR (pre-rendered by the caller into `model.qrSvg`). */
export interface A4QrElement extends ElementBase {
  type: 'qr';
}

/**
 * The invoice number as a Code 128 barcode (pre-rendered by the caller into
 * `model.barcodeSvg`, like the QR) — the legacy till's A4 sheet prints one
 * beside the header so a scanner can find the receipt.
 */
export interface A4BarcodeElement extends ElementBase {
  type: 'barcode';
  /** Fill the box in both directions (tall bars) instead of keeping the code's own proportions. */
  stretch?: boolean;
}

export interface A4SignatureElement extends ElementBase {
  type: 'signature';
  labelEn?: string;
  labelAr?: string;
  /** Where the signing line sits relative to the labels. Default 'above'. */
  linePosition?: 'above' | 'below' | 'none';
}

export type A4ItemsField =
  | 'index' | 'code' | 'barcode' | 'name' | 'name_ar' | 'name_bilingual'
  | 'unit' | 'qty' | 'unit_price' | 'discount' | 'net' | 'tax' | 'gross' | 'total'
  | 'note' | 'salesman';

export interface A4ItemsColumn {
  id: string;
  field: A4ItemsField;
  labelEn?: string;
  labelAr?: string;
  widthPct?: number;
  align?: A4Align;
  /**
   * Money columns only: print just the integer part ('major', "15") or just
   * the fraction ('minor', "000" on a 3-decimal currency) — the K.D / Fils
   * pair. Unset prints the whole amount.
   */
  part?: 'major' | 'minor';
  /**
   * Columns sharing a group id get one spanning header above their own
   * labels ("سعر الوحدة" over "K.D | Fills"). The group's labels are read
   * from the FIRST column carrying the id.
   */
  group?: string;
  groupLabelEn?: string;
  groupLabelAr?: string;
  /** Quantity columns: decimals to print. Default 3 (the ERP's "1.000"). */
  decimals?: number;
  /** Quantity columns: drop trailing zeros — "1" not "1.000", "1.5" not "1.500". */
  trimZeros?: boolean;
  /**
   * This column's body cells only, laid over the table's `rowStyle` — a bold
   * small name beside regular figures. `align` here overrides the column's
   * `align` for the cells (the header keeps it); `valign` sets the cells'
   * vertical alignment.
   */
  style?: A4TextStyle;
}

export interface A4ItemsTotalRow {
  show: boolean;
  labelEn?: string;
  labelAr?: string;
  /**
   * Index of the column the label starts in; it spans up to the first money
   * total column. Columns before it are filled with `fillBackground`.
   */
  labelFromCol?: number;
  fillBackground?: string;
  style?: A4TextStyle;
}

export interface A4ItemsElement extends ElementBase {
  type: 'items';
  columns: A4ItemsColumn[];
  showHeader?: boolean;
  headerStyle?: A4TextStyle;
  rowStyle?: A4TextStyle;
  /** Minimum row height, mm. */
  rowMinHeightMm?: number;
  borders?: 'grid' | 'horizontal' | 'outer' | 'none';
  borderColor?: string;
  /** For 'name' columns: also print the Arabic name under the English one. */
  showArabicName?: boolean;
  showModifiers?: boolean;
  showNote?: boolean;
  /** Sum row under the lines ("Total Amount"). */
  totalRow?: A4ItemsTotalRow;
  /**
   * Blank rows appended so a short invoice still fills the same box the
   * tenant's pre-printed stationery has. Default 0.
   */
  minRows?: number;
}

export type A4TotalsKey =
  | 'subtotal' | 'discount' | 'delivery' | 'after_discount' | 'tax'
  | 'total' | 'paid' | 'change' | 'in_words' | 'in_words_ar';

export interface A4TotalsRow {
  key: A4TotalsKey;
  labelEn?: string;
  labelAr?: string;
  bold?: boolean;
  fontSizePt?: number;
  background?: string;
  /** Fixed row height, mm. Unset lets the row size to its text, as before. */
  heightMm?: number;
  /** Printed before the amount — "+ " on a delivery row. Not with `moneySplit`. */
  prefix?: string;
}

export interface A4TotalsElement extends ElementBase {
  type: 'totals';
  rows: A4TotalsRow[];
  /** Skip rows whose value is zero. Default true. */
  hideZero?: boolean;
  /** Print money as two cells (major | minor) instead of one. */
  moneySplit?: boolean;
  borders?: 'grid' | 'horizontal' | 'none';
  labelStyle?: A4TextStyle;
  valueStyle?: A4TextStyle;
  /** Label column width, percent of the element. Default 60. */
  labelWidthPct?: number;
  /** Colour of the rules. Default black. */
  borderColor?: string;
}

export interface A4PaymentsElement extends ElementBase {
  type: 'payments';
  titleEn?: string;
  titleAr?: string;
  showChange?: boolean;
}

/**
 * One cell of an `A4GridElement`. A cell with a `token` prints that value; a
 * cell without one is a CAPTION and takes the grid's label styling and fill.
 */
export interface A4GridCell {
  /** Token whose value fills the cell. Omit to make this a caption cell. */
  token?: string;
  labelAr?: string;
  labelEn?: string;
  /** Share of the table's width. Taken from the FIRST row; make it sum to 100. */
  widthPct?: number;
  align?: A4Align;
  /** Let a long value wrap onto more lines instead of being clipped. */
  wrap?: boolean;
  /** Columns spanned. Default 1. */
  span?: number;
}

export interface A4GridRow {
  cells: A4GridCell[];
  /** Minimum height, mm. The row still grows to fit a wrapping cell. */
  heightMm?: number;
}

/**
 * A caption/value TABLE — the boxed header grid every one of these ERP forms
 * opens with ("الفرع │ فرع الرى │ رقم الفاتوره │ 3937").
 *
 * It exists because the same thing built out of `field` elements cannot be
 * made to look right: each one is absolutely positioned with its own border,
 * so neighbours draw two lines where the eye expects one and any rounding
 * leaves them a hair apart — the first TAXIAT prints showed exactly that. One
 * `<table>` with `border-collapse: collapse` links every cell by
 * construction, keeps a row's cells the same height however much text one of
 * them holds, and takes its column widths from the design rather than from
 * whichever label happens to be longest.
 */
export interface A4GridElement extends ElementBase {
  type: 'grid';
  rows: A4GridRow[];
  borders?: 'grid' | 'outer' | 'none';
  borderColor?: string;
  /** Default minimum row height, mm. */
  rowMinHeightMm?: number;
  labelStyle?: A4TextStyle;
  /** Fill behind caption cells. */
  labelBackground?: string;
  valueStyle?: A4TextStyle;
}

export type A4Element =
  | A4TextElement | A4FieldElement | A4LogoElement | A4ImageElement
  | A4LineElement | A4RectElement | A4QrElement | A4BarcodeElement | A4SignatureElement
  | A4ItemsElement | A4TotalsElement | A4PaymentsElement | A4GridElement;

export type A4ElementType = A4Element['type'];

/**
 * A faded picture printed behind everything on the sheet — usually the
 * company's logo across the middle of the invoice. It is a PAGE setting, not
 * an element, so it is always underneath: no element can be ordered behind it.
 */
export interface A4Watermark {
  /** The uploaded image. Empty = no watermark. */
  dataUrl: string;
  /** 0–1. Default 0.15. Use 1 for an image that is already pale. */
  opacity?: number;
  /** Size, mm. Default: 140 mm wide, height from the image. */
  widthMm?: number;
  heightMm?: number;
  /** Top-left corner, mm. Default: centred on the sheet. */
  x?: number;
  y?: number;
}

export interface A4Page {
  widthMm: number;
  heightMm: number;
  fontFamily?: string;
  baseFontPt?: number;
  /** 'auto' uses the model's resolved direction. */
  direction?: 'ltr' | 'rtl' | 'auto';
  color?: string;
  background?: string;
  watermark?: A4Watermark;
}

export interface A4Design {
  version: 1;
  page: A4Page;
  elements: A4Element[];
}

/** Per-tenant config carried on PrintConfig. */
export interface A4DesignConfig {
  enabled: boolean;
  design: A4Design | null;
}

// ── Render model ───────────────────────────────────────────────────────────

export interface A4RenderItem {
  code?: string | null;
  barcode?: string | null;
  name: string;
  nameAr?: string | null;
  unit?: string | null;
  qty: number;
  /** Per-unit price before line discount, ex-VAT. */
  unitPrice: number;
  /** Line discount value. */
  discount: number;
  /** qty × unitPrice − discount, ex-VAT, after its share of the header discount. */
  net: number;
  /** VAT on `net`. */
  tax: number;
  /** net + tax. */
  gross: number;
  /** What the line total column prints: the line's value as the till sees it. */
  total: number;
  mods?: string[];
  note?: string | null;
  salesman?: string | null;
  isReturn?: boolean;
}

export interface A4RenderTotals {
  subtotal: number;
  discount: number;
  delivery: number;
  afterDiscount: number;
  tax: number;
  total: number;
  paid: number;
  change: number;
}

export interface A4RenderPayment {
  label: string;
  amount: number;
}

export interface A4RenderModel {
  /** token -> already-formatted, human-readable string. */
  fields: Record<string, string>;
  items: A4RenderItem[];
  totals: A4RenderTotals;
  /** Amount in words, both scripts; empty when the caller cannot spell it. */
  totalInWords: { en: string; ar: string };
  currency: { code: string; precision: number };
  payments: A4RenderPayment[];
  /**
   * Automatic promotions this receipt matched, named: the Arabic and English
   * name and the NUMERIC amount (money here is numeric — see `moneySplit`).
   * Their money is part of `totals.discount`; the totals element prints them
   * where the design put its Discount row and gives that row only the unnamed
   * rest. Absent on a receipt without promotions — the design prints exactly
   * what it did.
   */
  promotions?: Array<{ nameEn: string; nameAr: string; amount: number }>;
  logoDataUrl?: string | null;
  menuLogoDataUrl?: string | null;
  /** The ZATCA QR as an inline <svg> string, or null when there is no VAT number. */
  qrSvg?: string | null;
  /** The invoice number as a Code 128 <svg>, for a `barcode` element. */
  barcodeSvg?: string | null;
  direction: 'ltr' | 'rtl';
  /**
   * The till is running in Arabic, so every field LABEL on this sheet prints
   * its Arabic text alone. An English session prints both — Arabic over
   * English — which is the paper a Gulf customer already receives. The rule
   * is the same one the built-in A4 sheet follows, and it applies to a saved
   * design too: the labels a design stores are its Arabic and English words
   * for one label, not two independent strings, so which of them shows is the
   * reader's choice and not the designer's.
   *
   * Absent on a model built by an older caller — treated as false (bilingual),
   * which is exactly what those sheets printed.
   */
  arabicLabels?: boolean;
  printOptions?: ReceiptPrintOptions | null;
  isReprint?: boolean;
}

// ── Field token catalogue (drives the admin "insert field" menu) ────────────

export interface A4TokenDef {
  token: string;
  labelEn: string;
  labelAr: string;
  sample: string;
  /** Grouping in the admin palette. */
  group: 'document' | 'company' | 'customer' | 'totals' | 'other';
}

export const A4_FIELD_TOKENS: readonly A4TokenDef[] = [
  { token: 'document_title', labelEn: 'Document title', labelAr: 'عنوان المستند', sample: 'Cash Sales', group: 'document' },
  { token: 'document_title_ar', labelEn: 'Document title (Arabic)', labelAr: 'عنوان المستند (عربي)', sample: 'مبيعات نقدية', group: 'document' },
  { token: 'order_no', labelEn: 'Invoice number', labelAr: 'رقم الفاتورة', sample: '7042', group: 'document' },
  { token: 'receipt_no', labelEn: 'Receipt number (payment voucher)', labelAr: 'رقم سند القبض', sample: '', group: 'document' },
  { token: 'reference', labelEn: 'Reference (return: other leg)', labelAr: 'المرجع', sample: '', group: 'document' },
  { token: 'order_date', labelEn: 'Date', labelAr: 'التاريخ', sample: '21-07-2026', group: 'document' },
  { token: 'order_time', labelEn: 'Time', labelAr: 'الوقت', sample: '14:22', group: 'document' },
  { token: 'datetime', labelEn: 'Date & time', labelAr: 'التاريخ والوقت', sample: '21-07-2026 14:22', group: 'document' },
  { token: 'datetime_seconds', labelEn: 'Date & time (12-hour, seconds)', labelAr: 'التاريخ والوقت (١٢ ساعة بالثواني)', sample: '21-07-2026 2:22:05 pm', group: 'document' },
  { token: 'datetime_short', labelEn: 'Date & time (dd/mm/yyyy hh:mm)', labelAr: 'التاريخ والوقت (يوم/شهر/سنة)', sample: '21/07/2026 14:22', group: 'document' },
  { token: 'order_type', labelEn: 'Order type', labelAr: 'نوع الطلب', sample: 'Takeaway', group: 'document' },
  { token: 'order_type_ar', labelEn: 'Order type (Arabic)', labelAr: 'نوع الطلب (عربي)', sample: 'سفري', group: 'document' },
  { token: 'delivery_date', labelEn: 'Delivery date & time', labelAr: 'تاريخ ووقت التوصيل', sample: '2026-07-21 02:30 PM', group: 'document' },
  { token: 'memo', labelEn: 'Memo (as stored on the receipt)', labelAr: 'البيان (كما هو مسجّل على الفاتورة)', sample: '', group: 'document' },
  { token: 'reference_no', labelEn: 'Reference no. (till)', labelAr: 'المرجع', sample: '000055', group: 'document' },
  { token: 'notes', labelEn: 'Notes (till)', labelAr: 'الملاحظات', sample: 'رفع قياس خارجي', group: 'document' },
  { token: 'payments', labelEn: 'Payment summary (method + amount)', labelAr: 'ملخص الدفع (الطريقة والمبلغ)', sample: 'Cash 15.000', group: 'document' },
  { token: 'payments_colon', labelEn: 'Payment summary (method :amount)', labelAr: 'ملخص الدفع (الطريقة :المبلغ)', sample: 'Cash :15.000', group: 'document' },
  { token: 'payment_methods', labelEn: 'Payment method(s), no amounts', labelAr: 'طريقة الدفع (بدون مبالغ)', sample: 'Knet', group: 'document' },
  { token: 'table', labelEn: 'Table label', labelAr: 'الطاولة', sample: '', group: 'document' },
  { token: 'cashier_name', labelEn: 'Cashier', labelAr: 'الكاشير', sample: 'Ahmed', group: 'document' },
  { token: 'salesman_name', labelEn: 'Salesman', labelAr: 'المندوب', sample: '', group: 'document' },
  { token: 'reprint_label', labelEn: 'REPRINT mark (empty on first print)', labelAr: 'علامة إعادة الطباعة', sample: '', group: 'document' },
  { token: 'print_date', labelEn: 'Print date & time', labelAr: 'وقت الطباعة', sample: '21-07-2026 14:25', group: 'document' },

  { token: 'company_name', labelEn: 'Company name', labelAr: 'اسم الشركة', sample: 'Kuwaitec Al Deera For Agricultural Material And Equipment Co.', group: 'company' },
  { token: 'company_name_ar', labelEn: 'Company name (Arabic)', labelAr: 'اسم الشركة (عربي)', sample: 'شركة كويتك الديرة لمواد والمعدات الزراعية', group: 'company' },
  { token: 'company_address', labelEn: 'Company address', labelAr: 'عنوان الشركة', sample: 'Abdali, Kuwait', group: 'company' },
  { token: 'company_address_ar', labelEn: 'Company address (Arabic)', labelAr: 'عنوان الشركة (عربي)', sample: 'العبدلي، الكويت', group: 'company' },
  { token: 'company_contact', labelEn: 'Company contact lines', labelAr: 'أرقام التواصل', sample: 'Administration: 966635343\\99734455\nAbdali Branch: 60643133\\60060915\nAl Wafra Branch: 55240399\\66245320', group: 'company' },
  { token: 'company_contact_ar', labelEn: 'Company contact lines (Arabic)', labelAr: 'أرقام التواصل (عربي)', sample: 'الإدارة: 99734455\\966635343\nفرع العبدلي: 60060915\\60643133\nفرع الوفرة: 66245320\\55240399', group: 'company' },
  { token: 'branch_name', labelEn: 'Branch name', labelAr: 'اسم الفرع', sample: 'Abdali Branch', group: 'company' },
  { token: 'branch_name_ar', labelEn: 'Branch name (Arabic)', labelAr: 'اسم الفرع (عربي)', sample: 'فرع العبدلي', group: 'company' },
  { token: 'branch_address', labelEn: 'Branch address', labelAr: 'عنوان الفرع', sample: '', group: 'company' },
  { token: 'tax_number', labelEn: 'Tax / VAT number', labelAr: 'الرقم الضريبي', sample: '', group: 'company' },
  { token: 'cr_number', labelEn: 'Commercial reg. no.', labelAr: 'السجل التجاري', sample: '', group: 'company' },

  { token: 'customer_name', labelEn: 'Customer name', labelAr: 'اسم العميل', sample: 'مزرعة / عيد مبارك العازمي', group: 'customer' },
  { token: 'customer_name_ar', labelEn: 'Customer name (Arabic)', labelAr: 'اسم العميل (عربي)', sample: 'مزرعة / عيد مبارك العازمي', group: 'customer' },
  { token: 'customer_phone', labelEn: 'Customer phone', labelAr: 'هاتف العميل', sample: '99001122', group: 'customer' },
  { token: 'customer_address', labelEn: 'Customer address', labelAr: 'عنوان العميل', sample: '', group: 'customer' },
  { token: 'customer_tax_number', labelEn: 'Customer VAT number', labelAr: 'الرقم الضريبي للعميل', sample: '', group: 'customer' },

  { token: 'subtotal', labelEn: 'Subtotal', labelAr: 'الإجمالي الفرعي', sample: '15.000', group: 'totals' },
  { token: 'discount', labelEn: 'Discount', labelAr: 'الخصم', sample: '', group: 'totals' },
  { token: 'promotion_names', labelEn: 'Promotion name(s)', labelAr: 'اسم العرض', sample: 'Morning mix', group: 'totals' },
  { token: 'delivery', labelEn: 'Delivery fee', labelAr: 'رسوم التوصيل', sample: '', group: 'totals' },
  { token: 'tax', labelEn: 'Tax', labelAr: 'الضريبة', sample: '', group: 'totals' },
  { token: 'tax_percent', labelEn: 'Tax rate %', labelAr: 'نسبة الضريبة', sample: '0', group: 'totals' },
  { token: 'total', labelEn: 'Total', labelAr: 'الإجمالي', sample: '15.000', group: 'totals' },
  { token: 'paid', labelEn: 'Paid', labelAr: 'المدفوع', sample: '15.000', group: 'totals' },
  { token: 'change', labelEn: 'Change', labelAr: 'الباقي', sample: '', group: 'totals' },
  { token: 'change_amount', labelEn: 'Change (always, 0.000 too)', labelAr: 'الباقي (دائماً، حتى 0.000)', sample: '', group: 'totals' },
  { token: 'total_in_words', labelEn: 'Total in words', labelAr: 'الإجمالي كتابةً', sample: 'Fifteen Kuwaiti Dinars only', group: 'totals' },
  { token: 'total_in_words_ar', labelEn: 'Total in words (Arabic)', labelAr: 'الإجمالي كتابةً (عربي)', sample: 'خمسة عشر دينار كويتي فقط لا غير', group: 'totals' },
  { token: 'currency', labelEn: 'Currency code', labelAr: 'رمز العملة', sample: 'KWD', group: 'totals' },
  { token: 'item_count', labelEn: 'Number of lines', labelAr: 'عدد الأصناف', sample: '1', group: 'other' },
];

// ── Default design: the cash-sales sheet the tenants' stationery uses ──────

let _uid = 0;
function eid(prefix: string): string {
  _uid += 1;
  return `${prefix}_${Date.now().toString(36)}_${_uid}`;
}

/** A fresh element of the given type at a sensible size — the designer's "add". */
export function newA4Element(type: A4ElementType, at: { x: number; y: number } = { x: 20, y: 20 }): A4Element {
  const base = { id: eid(type), x: at.x, y: at.y };
  switch (type) {
    case 'text':
      return { ...base, type, w: 60, h: 8, text: 'Text', style: { fontSizePt: 10 } };
    case 'field':
      return { ...base, type, w: 60, h: 8, token: 'order_no', labelEn: 'Invoice no.', labelLayout: 'inline', labelWidthMm: 22, valueFrame: { borderWidthPx: 1, borderColor: '#000', paddingMm: 1 } };
    case 'logo':
      return { ...base, type, w: 40, h: 20, source: 'receipt', fit: 'contain' };
    case 'image':
      return { ...base, type, w: 40, h: 20, dataUrl: '', fit: 'contain' };
    case 'line':
      return { ...base, type, w: 100, h: 1, orientation: 'h', thicknessPx: 1, lineStyle: 'solid', color: '#000' };
    case 'rect':
      return { ...base, type, w: 60, h: 20, frame: { borderWidthPx: 1, borderColor: '#000' } };
    case 'grid':
      return {
        ...base, type, w: 180, h: 20,
        borders: 'grid', borderColor: '#000', rowMinHeightMm: 8,
        labelBackground: '#dcdcdc',
        labelStyle: { fontSizePt: 9, bold: true },
        valueStyle: { fontSizePt: 9 },
        rows: [
          { cells: [
            { labelEn: 'Invoice no.', labelAr: 'رقم الفاتورة', widthPct: 20 },
            { token: 'order_no', widthPct: 30 },
            { labelEn: 'Date', labelAr: 'التاريخ', widthPct: 20 },
            { token: 'order_date', widthPct: 30 },
          ] },
          { cells: [
            { labelEn: 'Customer', labelAr: 'العميل', widthPct: 20 },
            { token: 'customer_name', widthPct: 30 },
            { labelEn: 'Phone', labelAr: 'رقم الهاتف', widthPct: 20 },
            { token: 'customer_phone', widthPct: 30 },
          ] },
        ],
      };
    case 'qr':
      return { ...base, type, w: 30, h: 30 };
    case 'barcode':
      return { ...base, type, w: 50, h: 16 };
    case 'signature':
      return { ...base, type, w: 45, h: 14, labelEn: 'Signature', labelAr: 'التوقيع', linePosition: 'above' };
    case 'items':
      return {
        ...base, type, w: 190, h: 40, showHeader: true, borders: 'grid',
        columns: [
          { id: eid('c'), field: 'name', labelEn: 'Item', labelAr: 'الصنف', widthPct: 50, align: 'left' },
          { id: eid('c'), field: 'qty', labelEn: 'Qty', labelAr: 'الكمية', widthPct: 14, align: 'center' },
          { id: eid('c'), field: 'unit_price', labelEn: 'Price', labelAr: 'السعر', widthPct: 18, align: 'right' },
          { id: eid('c'), field: 'total', labelEn: 'Total', labelAr: 'الإجمالي', widthPct: 18, align: 'right' },
        ],
        totalRow: { show: true, labelEn: 'Total', labelAr: 'الإجمالي' },
      };
    case 'totals':
      return {
        ...base, type, w: 80, h: 30, hideZero: true, borders: 'horizontal',
        rows: [
          { key: 'subtotal', labelEn: 'Subtotal', labelAr: 'الإجمالي الفرعي' },
          { key: 'discount', labelEn: 'Discount', labelAr: 'الخصم' },
          { key: 'tax', labelEn: 'Tax', labelAr: 'الضريبة' },
          { key: 'total', labelEn: 'Total', labelAr: 'الإجمالي', bold: true },
        ],
      };
    case 'payments':
      return { ...base, type, w: 80, h: 20, titleEn: 'Payments', titleAr: 'المدفوعات', showChange: true };
    default:
      return { ...base, type: 'text', w: 60, h: 8, text: '' };
  }
}

/**
 * The default A4 design — the bilingual Gulf "cash sales" sheet: company name
 * and phone lines in both scripts across the top, the document title centred,
 * boxed invoice / receipt numbers on the left and date / branch / memo on the
 * right, a two-row customer block, an items table with dinar and fils
 * columns, a Total Amount row, a receipt-in-good-condition line and two
 * signature strips. Millimetres were read off the reference print.
 */
export function defaultA4Design(): A4Design {
  const black = '#000';
  const box: A4Frame = { borderWidthPx: 1, borderColor: black, paddingMm: 1 };
  const headerBg = '#3f3f3f';
  return {
    version: 1,
    page: { widthMm: 210, heightMm: 297, baseFontPt: 10, direction: 'ltr' },
    elements: [
      // ── Masthead ──
      { id: 'company_en', type: 'text', x: 10, y: 12, w: 92, h: 12, text: '{{company_name}}', style: { fontSizePt: 11, bold: true, align: 'left' }, name: 'Company name (EN)' },
      { id: 'company_ar', type: 'text', x: 108, y: 12, w: 92, h: 8, text: '{{company_name_ar}}', style: { fontSizePt: 10, bold: true, align: 'right', direction: 'rtl' }, name: 'Company name (AR)' },
      { id: 'contact_en', type: 'text', x: 10, y: 25, w: 92, h: 16, text: '{{company_contact}}', hideIfEmpty: 'company_contact', style: { fontSizePt: 9.5, bold: true, align: 'left', lineHeight: 1.25 }, name: 'Contact lines (EN)' },
      { id: 'contact_ar', type: 'text', x: 108, y: 22, w: 92, h: 16, text: '{{company_contact_ar}}', hideIfEmpty: 'company_contact_ar', style: { fontSizePt: 9, align: 'right', direction: 'rtl', lineHeight: 1.3 }, name: 'Contact lines (AR)' },

      // ── Title ──
      { id: 'title', type: 'text', x: 75, y: 46, w: 60, h: 9, text: '{{document_title_ar}}', style: { fontSizePt: 14, bold: true, align: 'center', direction: 'rtl' }, name: 'Title' },

      // ── Left fields ──
      { id: 'invoice_no', type: 'field', x: 10, y: 46, w: 62, h: 9, token: 'order_no', labelEn: 'Invoice no.', labelLayout: 'inline', labelWidthMm: 24, valueFrame: box, labelStyle: { fontSizePt: 10 }, valueStyle: { fontSizePt: 10, align: 'left' }, name: 'Invoice no.' },
      { id: 'receipt_no', type: 'field', x: 10, y: 58, w: 62, h: 9, token: 'receipt_no', labelEn: 'Receipt no.', labelLayout: 'inline', labelWidthMm: 24, valueFrame: box, labelStyle: { fontSizePt: 10 }, valueStyle: { fontSizePt: 10, align: 'left' }, name: 'Receipt no.' },

      // ── Right fields ──
      { id: 'date', type: 'text', x: 146, y: 43, w: 54, h: 8, text: 'Date: {{order_date}} :التاريخ', frame: box, style: { fontSizePt: 9.5, bold: true, align: 'center', valign: 'middle' }, name: 'Date' },
      { id: 'branch', type: 'field', x: 146, y: 53, w: 54, h: 8, token: 'branch_name_ar', labelEn: 'Branch', labelLayout: 'inline', labelWidthMm: 14, valueFrame: box, labelStyle: { fontSizePt: 9.5 }, valueStyle: { fontSizePt: 9, align: 'right', direction: 'rtl' }, name: 'Branch' },
      { id: 'memo', type: 'field', x: 146, y: 63, w: 54, h: 8, token: 'memo', labelEn: 'Memo', labelLayout: 'inline', labelWidthMm: 14, valueFrame: box, labelStyle: { fontSizePt: 9.5 }, valueStyle: { fontSizePt: 8.5, align: 'left' }, name: 'Memo' },

      // ── Customer block ──
      { id: 'customer', type: 'field', x: 10, y: 76, w: 190, h: 8, token: 'customer_name', labelEn: 'Mr./Messrs:', labelAr: 'السيد / السادة:', labelLayout: 'both-ends', labelWidthMm: 36, frame: { borderWidthPx: 1, borderColor: black }, labelStyle: { fontSizePt: 9.5 }, valueStyle: { fontSizePt: 9.5, align: 'center' }, name: 'Customer' },
      { id: 'address', type: 'field', x: 10, y: 84, w: 190, h: 8, token: 'customer_address', labelEn: 'Address:', labelAr: 'العنوان:', labelLayout: 'both-ends', labelWidthMm: 36, frame: { borderWidthPx: 1, borderColor: black }, labelStyle: { fontSizePt: 9.5 }, valueStyle: { fontSizePt: 9.5, align: 'center' }, name: 'Address' },

      // ── Items ──
      {
        id: 'items', type: 'items', x: 10, y: 92, w: 190, h: 30,
        showHeader: true, borders: 'grid', borderColor: black, rowMinHeightMm: 6,
        headerStyle: { fontSizePt: 9, bold: true, background: headerBg, color: '#fff', align: 'center' },
        rowStyle: { fontSizePt: 9.5, align: 'center' },
        showArabicName: false,
        columns: [
          { id: 'c_name', field: 'name', labelEn: 'Items', labelAr: 'الصنف', widthPct: 46, align: 'center' },
          { id: 'c_qty', field: 'qty', labelEn: 'Qty', labelAr: 'الكمية', widthPct: 11, align: 'center', decimals: 3 },
          { id: 'c_up_major', field: 'unit_price', part: 'major', labelEn: 'K.D', labelAr: 'دينار', widthPct: 11, align: 'center', group: 'unit_price', groupLabelEn: '', groupLabelAr: 'سعر الوحدة' },
          { id: 'c_up_minor', field: 'unit_price', part: 'minor', labelEn: 'Fills', labelAr: 'فلس', widthPct: 10, align: 'center', group: 'unit_price' },
          { id: 'c_tot_major', field: 'total', part: 'major', labelEn: 'K.D', labelAr: 'دينار', widthPct: 11, align: 'center', group: 'total', groupLabelEn: '', groupLabelAr: 'الإجمالي' },
          { id: 'c_tot_minor', field: 'total', part: 'minor', labelEn: 'Fills', labelAr: 'فلس', widthPct: 11, align: 'center', group: 'total' },
        ],
        totalRow: { show: true, labelEn: 'Total Amount', labelAr: 'القيمة الاجمالية', labelFromCol: 2, fillBackground: '#a6a6a6', style: { bold: true, fontSizePt: 9.5 } },
        name: 'Items table',
      },

      // ── Below the table ──
      { id: 'received_note', type: 'text', anchor: 'after-items', x: 10, y: 14, w: 190, h: 7, text: 'استلمت الأصناف المذكورة أعلاه بحالة جيدة', style: { fontSizePt: 10, bold: true, align: 'center', direction: 'rtl' }, name: 'Received-in-good-condition line' },
      { id: 'sig_right', type: 'signature', anchor: 'after-items', x: 150, y: 40, w: 45, h: 14, labelEn: 'signature', labelAr: 'التوقيع', linePosition: 'none', style: { fontSizePt: 9.5, align: 'center' }, name: 'Signature' },
      { id: 'sig_left', type: 'signature', anchor: 'after-items', x: 15, y: 40, w: 45, h: 14, labelEn: 'Received By', labelAr: 'اسم المستلم', linePosition: 'none', style: { fontSizePt: 9.5, align: 'center' }, name: 'Received by' },
    ],
  };
}

// ── Renderer ────────────────────────────────────────────────────────────────

function esc(s: unknown): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function applyTokens(template: string, fields: Record<string, string>): string {
  return esc(template).replace(/\{\{\s*([a-z0-9_]+)\s*\}\}/gi, (_m, name: string) => {
    const v = fields[name];
    return v == null ? '' : esc(v);
  });
}

function tokenEmpty(token: string | undefined, fields: Record<string, string>): boolean {
  if (!token) return false;
  const v = fields[token];
  return v == null || String(v).trim() === '';
}

function mm(n: number): string {
  return `${Math.round(n * 100) / 100}mm`;
}

function textCss(s: A4TextStyle | undefined): string {
  if (!s) return '';
  const out: string[] = [];
  if (s.fontFamily) out.push(`font-family:${s.fontFamily}`);
  if (s.fontSizePt) out.push(`font-size:${s.fontSizePt}pt`);
  if (s.bold) out.push('font-weight:700');
  if (s.italic) out.push('font-style:italic');
  if (s.underline) out.push('text-decoration:underline');
  if (s.align) out.push(`text-align:${s.align}`);
  if (s.color) out.push(`color:${s.color}`);
  if (s.background) out.push(`background:${s.background}`);
  if (s.lineHeight) out.push(`line-height:${s.lineHeight}`);
  if (s.direction) out.push(`direction:${s.direction}`);
  if (s.uppercase) out.push('text-transform:uppercase');
  return out.join(';');
}

function frameCss(f: A4Frame | undefined): string {
  if (!f) return '';
  const out: string[] = [];
  if (f.borderWidthPx) out.push(`border:${f.borderWidthPx}px ${f.borderStyle ?? 'solid'} ${f.borderColor ?? '#000'}`);
  if (f.radiusMm) out.push(`border-radius:${f.radiusMm}mm`);
  if (f.paddingMm != null) out.push(`padding:${f.paddingMm}mm`);
  if (f.background) out.push(`background:${f.background}`);
  return out.join(';');
}

function valignCss(s: A4TextStyle | undefined): string {
  const v = s?.valign;
  if (!v || v === 'top') return '';
  return `display:flex;flex-direction:column;justify-content:${v === 'middle' ? 'center' : 'flex-end'}`;
}

/** Position + size box of an element (page-anchored → absolute on the sheet). */
function boxCss(e: A4Element, extra = ''): string {
  return [
    'position:absolute',
    `left:${mm(e.x)}`,
    `top:${mm(e.y)}`,
    `width:${mm(e.w)}`,
    e.type === 'items' || e.type === 'totals' || e.type === 'payments' ? `min-height:${mm(e.h)}` : `height:${mm(e.h)}`,
    textCss(e.style),
    frameCss(e.frame),
    valignCss(e.style),
    e.opacity != null && e.opacity < 1 ? `opacity:${Math.max(0, e.opacity)}` : '',
    extra,
  ].filter(Boolean).join(';');
}

/**
 * One positioned element. A rotated one keeps its box on the sheet exactly
 * where the designer drew it; its content is laid out in a box of the TURNED
 * size (a tall narrow box holds one long line) and turned about the centre.
 */
function elementHtml(e: A4Element, model: A4RenderModel): string {
  const inner = renderElementInner(e, model);
  const r = e.rotate ?? 0;
  if (!r) return `<div class="el el-${e.type}" style="${boxCss(e)}">${inner}</div>`;
  const sideways = r === 90 || r === 270;
  const w = sideways ? e.h : e.w;
  const h = sideways ? e.w : e.h;
  return `<div class="el el-${e.type}" style="${boxCss(e)}"><div class="rot" style="width:${mm(w)};height:${mm(h)};transform:translate(-50%,-50%) rotate(${r}deg);${valignCss(e.style)}">${inner}</div></div>`;
}

/** The page watermark, drawn first so everything else is on top of it. */
function watermarkHtml(page: A4Page, W: number, H: number): string {
  const wm = page.watermark;
  if (!wm?.dataUrl) return '';
  const w = wm.widthMm ?? 140;
  // Without a height the image is fitted inside a square, keeping its shape.
  const h = wm.heightMm ?? w;
  const x = wm.x ?? (W - w) / 2;
  const y = wm.y ?? (H - h) / 2;
  const opacity = Math.min(1, Math.max(0, wm.opacity ?? 0.15));
  return `<div class="wm" style="left:${mm(x)};top:${mm(y)};width:${mm(w)};height:${mm(h)};opacity:${opacity}"><img src="${esc(wm.dataUrl)}" alt="" /></div>`;
}

/** 1234.5 → "1,234.500" at the given precision. */
export function a4Money(n: number, precision: number): string {
  const safe = Number.isFinite(n) ? n : 0;
  return safe.toLocaleString('en-US', { minimumFractionDigits: precision, maximumFractionDigits: precision });
}

/**
 * Split an amount into its integer and fractional parts at the currency's
 * precision: 15.25 KWD → { major: "15", minor: "250" }. Computed in minor
 * units so 0.9995 rounds to 1.000, never to "0" and "1000".
 */
export function a4MoneyParts(n: number, precision: number): { major: string; minor: string } {
  const safe = Number.isFinite(n) ? n : 0;
  const scale = 10 ** precision;
  const units = Math.round(Math.abs(safe) * scale);
  const major = Math.floor(units / scale);
  const minor = units - major * scale;
  const sign = safe < 0 && units > 0 ? '-' : '';
  return {
    major: `${sign}${major.toLocaleString('en-US')}`,
    minor: precision > 0 ? String(minor).padStart(precision, '0') : '',
  };
}

function qtyText(n: number, decimals: number, trimZeros = false): string {
  return (Number.isFinite(n) ? n : 0).toLocaleString('en-US', { minimumFractionDigits: trimZeros ? 0 : decimals, maximumFractionDigits: decimals });
}

function moneyCell(value: number, part: 'major' | 'minor' | undefined, precision: number): string {
  if (!part) return a4Money(value, precision);
  const p = a4MoneyParts(value, precision);
  return part === 'major' ? p.major : p.minor;
}

const MONEY_FIELDS: ReadonlySet<A4ItemsField> = new Set(['unit_price', 'discount', 'net', 'tax', 'gross', 'total']);
const TOTAL_FIELDS: ReadonlySet<A4ItemsField> = new Set(['total', 'gross']);

function bilingual(
  ar: string | undefined,
  en: string | undefined,
  arabicOnly = false,
): string {
  const a = ar?.trim() ?? '';
  const e = en?.trim() ?? '';
  if (arabicOnly) return `<div class="ar">${esc(a || e)}</div>`;
  if (a && e) return `<div class="ar">${esc(a)}</div><div class="en">${esc(e)}</div>`;
  return `<div>${esc(a || e)}</div>`;
}

function renderField(e: A4FieldElement, model: A4RenderModel): string {
  const value = model.fields[e.token] ?? '';
  const labelCss = textCss(e.labelStyle);
  const valueCss = [textCss(e.valueStyle), frameCss(e.valueFrame)].filter(Boolean).join(';');
  const lw = e.labelWidthMm != null ? `flex:0 0 ${mm(e.labelWidthMm)}` : 'flex:0 0 auto';

  // Arabic session: ONE label, the Arabic one — even for a 'both-ends' field,
  // whose two ends are the two languages of the same label and not two
  // different things to say. A field whose design carries only an English
  // label keeps it: dropping it would leave the value unlabelled, which is
  // worse than a label in the other script.
  const arLabels = Boolean(model.arabicLabels);
  const layout = e.labelLayout ?? 'inline';
  // Arabic session: the Arabic label alone. English session: BOTH, Arabic
  // first — "المرجع / Reference". 'both-ends' already shows both by placing
  // them at the two ends of the row, so it keeps its own arrangement.
  const bothEnds = layout === 'both-ends';
  // ...and in an Arabic session that surviving label stays in the END slot,
  // which is where the design put the Arabic one. This used to collapse the
  // field to 'inline', which moved it to the START — the LEFT on an LTR sheet,
  // so the row read "value, then label" to the one person who reads Arabic.
  // kuwaitecunited's customer and address boxes are the case: on the English
  // sheet "السيد / السادة:" sits at the right end, and an Arabic till printed
  // it at the left. The label does not move when the language changes.
  const arAtEnd = arLabels && bothEnds && Boolean(e.labelAr);
  // A framed label is a CELL, and a cell's width is fixed by the design. So
  // its two languages STACK — Arabic line over English, the way the items
  // table's own headers do — instead of being joined with " / ". Joining them
  // makes the label twice as wide in an English session, and a fixed-width
  // cell cannot grow: the text simply runs out through its own border, which
  // is what the first TAXIAT print showed. Stacked, the grid's geometry is
  // the same in both languages and only its row height changes.
  const stackLabel = Boolean(e.labelFrame) && !arLabels && Boolean(e.labelAr && e.labelEn);
  const startText = arAtEnd
    ? undefined
    : arLabels
      ? (e.labelAr || e.labelEn)
      : bothEnds
        ? e.labelEn
        : [e.labelAr, e.labelEn].filter(Boolean).join(' / ') || undefined;
  const startHtml = stackLabel
    ? `<span class="fl-lines"><span class="ar">${esc(e.labelAr!)}</span><span>${esc(e.labelEn!)}</span></span>`
    : esc(startText ?? '');
  const startDir = e.labelAr && (arLabels || !bothEnds) ? ' dir="rtl"' : '';
  // A framed label is a CELL: full height, its own border and fill, and its
  // text centred in it. Without a frame nothing changes — the span stays the
  // plain inline caption every existing design draws.
  const boxed = e.labelFrame ? frameCss(e.labelFrame) : '';
  const boxedCls = boxed ? ' fl-box' : '';
  const boxedJustify = boxed
    ? `;justify-content:${e.labelStyle?.align === 'right' ? 'flex-end' : e.labelStyle?.align === 'center' ? 'center' : 'flex-start'}`
    : '';
  const startLabel = startText
    ? `<span class="fl${boxedCls}"${startDir} style="${lw};${labelCss};${boxed}${boxedJustify}">${startHtml}</span>`
    // The dropped English caption leaves an EMPTY cell of its own width behind,
    // so the value keeps the position it has on the English sheet. Without it
    // a centred value would slide half a label's width toward the start and
    // the two languages of the same design would no longer line up.
    : arAtEnd
      ? `<span class="fl" style="${lw}"></span>`
      : '';
  const endLabel = e.labelAr && (arAtEnd || !arLabels)
    ? `<span class="fl fl-end${boxedCls}" dir="rtl" style="${lw};${labelCss};${boxed}${boxedJustify}">${esc(e.labelAr)}</span>`
    : '';
  const val = `<span class="fv${e.wrap ? ' fv-wrap' : ''}" style="${valueCss}">${esc(value)}</span>`;
  switch (layout) {
    case 'none':
      return `<div class="field">${val}</div>`;
    case 'stacked':
      return `<div class="field field-stacked"><span class="fl"${startDir} style="${labelCss}">${esc(startText ?? '')}</span>${val}</div>`;
    case 'both-ends':
      return `<div class="field field-inline">${startLabel}${val}${endLabel}</div>`;
    case 'inline':
    default:
      return `<div class="field field-inline">${startLabel || (!arLabels && e.labelAr ? `<span class="fl" dir="rtl" style="${lw};${labelCss}">${esc(e.labelAr)}</span>` : '')}${val}</div>`;
  }
}

function renderGrid(e: A4GridElement, model: A4RenderModel): string {
  const arLabels = Boolean(model.arabicLabels);
  const bc = e.borderColor ?? '#000';
  const borders = e.borders ?? 'grid';
  const cellBorder = borders === 'grid' ? `border:1px solid ${bc}` : '';
  const tableBorder = borders === 'outer' ? `border:1px solid ${bc}` : '';
  const labelCss = textCss(e.labelStyle);
  const valueCss = textCss(e.valueStyle);
  const labelBg = e.labelBackground ? `background:${e.labelBackground}` : '';
  const defaultH = e.rowMinHeightMm ? `height:${mm(e.rowMinHeightMm)}` : '';

  const rows = e.rows
    .map((r) => {
      const h = r.heightMm ? `height:${mm(r.heightMm)}` : defaultH;
      const cells = r.cells
        .map((c) => {
          const span = c.span && c.span > 1 ? ` colspan="${c.span}"` : '';
          const w = c.widthPct ? `width:${c.widthPct}%` : '';
          const align = c.align ? `text-align:${c.align}` : '';
          if (!c.token) {
            // A caption cell. Its two languages STACK in an English session,
            // so the column keeps the width the design gave it.
            const body = arLabels
              ? esc(c.labelAr || c.labelEn || '')
              : c.labelAr && c.labelEn
                ? `<div class="ar">${esc(c.labelAr)}</div><div>${esc(c.labelEn)}</div>`
                : esc(c.labelAr || c.labelEn || '');
            return `<td${span} style="${cellBorder};${labelCss};${w};${align};${labelBg};${h}">${body}</td>`;
          }
          const clip = c.wrap
            ? 'white-space:normal;overflow-wrap:break-word'
            : 'white-space:nowrap;overflow:hidden;text-overflow:ellipsis';
          return `<td${span} style="${cellBorder};${valueCss};${w};${align};${clip};${h}">${esc(model.fields[c.token] ?? '')}</td>`;
        })
        .join('');
      return `<tr>${cells}</tr>`;
    })
    .join('');

  return `<table class="a4grid" style="${tableBorder}">${rows}</table>`;
}

/** A column's own cell style, or '' so a column without one renders as before. */
function columnCss(c: A4ItemsColumn): string {
  if (!c.style) return '';
  const css = [textCss(c.style), c.style.valign ? `vertical-align:${c.style.valign}` : ''].filter(Boolean).join(';');
  return css ? `;${css}` : '';
}

function renderItems(e: A4ItemsElement, model: A4RenderModel): string {
  const arLabels = Boolean(model.arabicLabels);
  const cols = e.columns.length > 0 ? e.columns : (newA4Element('items') as A4ItemsElement).columns;
  const precision = model.currency.precision;
  const po = model.printOptions;
  const showArabicName = e.showArabicName ?? po?.bilingualItemName ?? false;
  const showMods = e.showModifiers ?? po?.printCustomOptions ?? false;
  const showNote = e.showNote ?? po?.printCustomOptions ?? false;
  const borders = e.borders ?? 'grid';
  const bc = e.borderColor ?? '#000';
  const cellBorder =
    borders === 'grid' ? `border:1px solid ${bc}` :
    borders === 'horizontal' ? `border-bottom:1px solid ${bc}` : '';
  const tableBorder = borders === 'outer' ? `border:1px solid ${bc}` : '';
  const headCss = textCss(e.headerStyle);
  const rowCss = textCss(e.rowStyle);
  const minH = e.rowMinHeightMm ? `height:${mm(e.rowMinHeightMm)}` : '';

  const colgroup = cols.map((c) => `<col${c.widthPct ? ` style="width:${c.widthPct}%"` : ''} />`).join('');

  // Header: one row, or two when any column belongs to a group.
  let head = '';
  if (e.showHeader !== false) {
    const grouped = cols.some((c) => c.group);
    if (!grouped) {
      head = `<thead><tr>${cols.map((c) => `<th style="${cellBorder};${headCss};text-align:${c.align ?? 'center'}">${bilingual(c.labelAr, c.labelEn, arLabels)}</th>`).join('')}</tr></thead>`;
    } else {
      const top: string[] = [];
      const bottom: string[] = [];
      let i = 0;
      while (i < cols.length) {
        const c = cols[i]!;
        if (!c.group) {
          top.push(`<th style="${cellBorder};${headCss}">${esc(c.labelAr ?? '')}</th>`);
          bottom.push(`<th style="${cellBorder};${headCss}">${esc(c.labelEn ?? '')}</th>`);
          i += 1;
          continue;
        }
        let span = 1;
        while (i + span < cols.length && cols[i + span]!.group === c.group) span += 1;
        top.push(`<th colspan="${span}" style="${cellBorder};${headCss}">${bilingual(c.groupLabelAr, c.groupLabelEn, arLabels)}</th>`);
        for (let k = 0; k < span; k += 1) {
          const gc = cols[i + k]!;
          const en = gc.labelEn?.trim() ?? '';
          const ar = gc.labelAr?.trim() ?? '';
          bottom.push(`<th style="${cellBorder};${headCss}">${esc(en)}${en && ar ? ' ' : ''}<span dir="rtl">${esc(ar)}</span></th>`);
        }
        i += span;
      }
      head = `<thead><tr>${top.join('')}</tr><tr>${bottom.join('')}</tr></thead>`;
    }
  }

  const cellValue = (c: A4ItemsColumn, it: A4RenderItem, idx: number): string => {
    switch (c.field) {
      case 'index': return String(idx + 1);
      case 'code': return esc(it.code ?? '');
      case 'barcode': return esc(it.barcode ?? '');
      case 'name': {
        const ar = showArabicName && it.nameAr && it.nameAr !== it.name ? `<div class="it-ar" dir="rtl">${esc(it.nameAr)}</div>` : '';
        return `<div class="it-name">${esc(it.name)}</div>${ar}`;
      }
      case 'name_ar': return `<span dir="rtl">${esc(it.nameAr || it.name)}</span>`;
      case 'name_bilingual': return `<div class="it-name">${esc(it.name)}</div>${it.nameAr && it.nameAr !== it.name ? `<div class="it-ar" dir="rtl">${esc(it.nameAr)}</div>` : ''}`;
      case 'unit': return esc(it.unit ?? '');
      case 'qty': return qtyText(it.qty, c.decimals ?? 3, c.trimZeros);
      case 'unit_price': return moneyCell(it.unitPrice, c.part, precision);
      case 'discount': return moneyCell(it.discount ?? 0, c.part, precision);
      case 'net': return moneyCell(it.net, c.part, precision);
      case 'tax': return moneyCell(it.tax, c.part, precision);
      case 'gross': return moneyCell(it.gross, c.part, precision);
      case 'total': return moneyCell(it.total, c.part, precision);
      case 'note': return esc(it.note ?? '');
      case 'salesman': return esc(it.salesman ?? '');
      default: return '';
    }
  };

  const ncols = cols.length;
  const rows = model.items.map((it, idx) => {
    const main = `<tr class="${it.isReturn ? 'ret' : ''}">${cols
      .map((c) => `<td style="${cellBorder};${rowCss};${minH};text-align:${c.align ?? (MONEY_FIELDS.has(c.field) ? 'right' : 'left')}${columnCss(c)}">${cellValue(c, it, idx)}</td>`)
      .join('')}</tr>`;
    const extras: string[] = [];
    if (showMods && it.mods && it.mods.length > 0) extras.push(`<tr><td class="sub" colspan="${ncols}" style="${cellBorder}">${esc(it.mods.join(' · '))}</td></tr>`);
    if (showNote && it.note && !cols.some((c) => c.field === 'note')) extras.push(`<tr><td class="sub" colspan="${ncols}" style="${cellBorder}">${esc(it.note)}</td></tr>`);
    return `<tbody class="it">${main}${extras.join('')}</tbody>`;
  });

  // Blank filler rows for pre-printed stationery.
  const filler: string[] = [];
  for (let k = model.items.length; k < (e.minRows ?? 0); k += 1) {
    filler.push(`<tr>${cols.map(() => `<td style="${cellBorder};${minH}">&nbsp;</td>`).join('')}</tr>`);
  }

  // Total row: sums of the money-total columns, the label spanning the columns
  // before them, filled cells before the label.
  let foot = '';
  if (e.totalRow?.show) {
    const firstTotal = cols.findIndex((c) => TOTAL_FIELDS.has(c.field));
    const labelFrom = Math.max(0, Math.min(e.totalRow.labelFromCol ?? (firstTotal > 0 ? firstTotal - 1 : 0), ncols - 1));
    const labelTo = firstTotal > labelFrom ? firstTotal : ncols; // exclusive
    const tCss = [textCss(e.totalRow.style), 'font-weight:700'].filter(Boolean).join(';');
    const fill = e.totalRow.fillBackground ? `background:${e.totalRow.fillBackground}` : '';
    const cells: string[] = [];
    if (labelFrom > 0) cells.push(`<td colspan="${labelFrom}" style="${cellBorder};${fill}"></td>`);
    cells.push(`<td colspan="${labelTo - labelFrom}" style="${cellBorder};${tCss};text-align:center">${bilingual(e.totalRow.labelAr, e.totalRow.labelEn, arLabels)}</td>`);
    const sum = model.items.reduce((s, it) => s + it.total, 0);
    const sumGross = model.items.reduce((s, it) => s + it.gross, 0);
    for (let k = labelTo; k < ncols; k += 1) {
      const c = cols[k]!;
      const v = c.field === 'gross' ? sumGross : c.field === 'total' ? sum : null;
      cells.push(`<td style="${cellBorder};${tCss};text-align:${c.align ?? 'right'}">${v == null ? '' : moneyCell(v, c.part, precision)}</td>`);
    }
    foot = `<tfoot><tr>${cells.join('')}</tr></tfoot>`;
  }

  return `<table class="items" style="${tableBorder}"><colgroup>${colgroup}</colgroup>${head}${rows.join('')}${filler.length ? `<tbody>${filler.join('')}</tbody>` : ''}${foot}</table>`;
}

const TOTALS_DEFAULT_LABELS: Record<A4TotalsKey, { en: string; ar: string }> = {
  subtotal: { en: 'Subtotal', ar: 'الإجمالي الفرعي' },
  discount: { en: 'Discount', ar: 'الخصم' },
  delivery: { en: 'Delivery', ar: 'التوصيل' },
  after_discount: { en: 'After discount', ar: 'الإجمالي بعد الخصم' },
  tax: { en: 'Tax', ar: 'الضريبة' },
  total: { en: 'TOTAL', ar: 'الإجمالي' },
  paid: { en: 'Paid', ar: 'المدفوع' },
  change: { en: 'Change', ar: 'الباقي' },
  in_words: { en: 'Amount in words', ar: 'المبلغ كتابةً' },
  in_words_ar: { en: 'Amount in words (AR)', ar: 'المبلغ كتابةً' },
};

function renderTotals(e: A4TotalsElement, model: A4RenderModel): string {
  const precision = model.currency.precision;
  const hideZero = e.hideZero !== false;
  const bc = e.borderColor ?? '#000';
  const cellBorder = e.borders === 'grid' ? `border:1px solid ${bc}` : e.borders === 'none' ? '' : `border-bottom:1px solid ${bc}`;
  const lw = e.labelWidthPct ?? 60;
  const moneyCells = (value: number, prefix?: string): string =>
    e.moneySplit
      ? (() => { const p = a4MoneyParts(value, precision); return `<td class="num" style="${cellBorder};${textCss(e.valueStyle)}">${p.major}</td><td class="num" style="${cellBorder};${textCss(e.valueStyle)}">${p.minor}</td>`; })()
      : `<td class="num" style="${cellBorder};${textCss(e.valueStyle)}">${prefix ? esc(prefix) : ''}${a4Money(value, precision)}</td>`;
  // Named promotions print where the design put its Discount row, in that
  // row's style (above TOTAL when it has none, at the end with neither), and
  // the Discount row keeps only the unnamed rest — every figure counts once.
  const promos = (model.promotions ?? []).filter((p) => p.amount > 0);
  const promoAnchor: A4TotalsKey | null = promos.length === 0
    ? null
    : e.rows.some((r) => r.key === 'discount')
      ? 'discount'
      : e.rows.some((r) => r.key === 'total') ? 'total' : null;
  const namedSum = promos.reduce((s, p) => s + p.amount, 0);
  const unnamedRest = model.totals.discount - namedSum > 0.005 ? model.totals.discount - namedSum : 0;
  const promoRows = (rowCss: string): string =>
    promos
      .map((p) => `<tr style="${rowCss}"><td style="${cellBorder};${textCss(e.labelStyle)}">${bilingual(p.nameAr, p.nameEn, Boolean(model.arabicLabels))}</td>${moneyCells(p.amount)}</tr>`)
      .join('');
  const rows = e.rows
    .map((r) => {
      const label = bilingual(r.labelAr ?? TOTALS_DEFAULT_LABELS[r.key].ar, r.labelEn ?? TOTALS_DEFAULT_LABELS[r.key].en, Boolean(model.arabicLabels));
      const rowCss = [r.bold ? 'font-weight:700' : '', r.fontSizePt ? `font-size:${r.fontSizePt}pt` : '', r.background ? `background:${r.background}` : '', r.heightMm ? `height:${mm(r.heightMm)}` : ''].filter(Boolean).join(';');
      if (r.key === 'in_words' || r.key === 'in_words_ar') {
        const words = r.key === 'in_words' ? model.totalInWords.en : model.totalInWords.ar;
        if (!words) return '';
        return `<tr style="${rowCss}"><td style="${cellBorder};${textCss(e.labelStyle)}">${label}</td><td colspan="${e.moneySplit ? 2 : 1}" style="${cellBorder};${textCss(e.valueStyle)}"${r.key === 'in_words_ar' ? ' dir="rtl"' : ''}>${esc(words)}</td></tr>`;
      }
      const isNamedDiscount = promos.length > 0 && r.key === 'discount';
      const value = isNamedDiscount ? unnamedRest : totalsValue(model.totals, r.key);
      const own = (hideZero || isNamedDiscount) && value === 0 && r.key !== 'total'
        ? ''
        : `<tr${r.heightMm ? ' class="fixed-h"' : ''} style="${rowCss}"><td style="${cellBorder};${textCss(e.labelStyle)}">${label}</td>${moneyCells(value, r.prefix)}</tr>`;
      if (promoAnchor !== r.key) return own;
      return promoAnchor === 'discount' ? own + promoRows(rowCss) : promoRows('') + own;
    })
    .filter(Boolean)
    .join('');
  const tail = promoAnchor == null ? promoRows('') : '';
  return `<table class="totals"><colgroup><col style="width:${lw}%" />${e.moneySplit ? `<col style="width:${(100 - lw) / 2}%" /><col style="width:${(100 - lw) / 2}%" />` : `<col style="width:${100 - lw}%" />`}</colgroup><tbody>${rows}${tail}</tbody></table>`;
}

function totalsValue(t: A4RenderTotals, key: A4TotalsKey): number {
  switch (key) {
    case 'subtotal': return t.subtotal;
    case 'discount': return t.discount;
    case 'delivery': return t.delivery;
    case 'after_discount': return t.afterDiscount;
    case 'tax': return t.tax;
    case 'total': return t.total;
    case 'paid': return t.paid;
    case 'change': return t.change;
    default: return 0;
  }
}

function renderPayments(e: A4PaymentsElement, model: A4RenderModel): string {
  const precision = model.currency.precision;
  const title = e.titleEn || e.titleAr ? `<div class="pay-title">${bilingual(e.titleAr, e.titleEn, Boolean(model.arabicLabels))}</div>` : '';
  const rows = model.payments.map((p) => `<tr><td>${esc(p.label)}</td><td class="num">${a4Money(p.amount, precision)}</td></tr>`).join('');
  const change = e.showChange !== false && model.totals.change > 0
    ? `<tr style="font-weight:700"><td>${bilingual('الباقي', 'Change', Boolean(model.arabicLabels))}</td><td class="num">${a4Money(model.totals.change, precision)}</td></tr>` : '';
  return `${title}<table class="pay"><tbody>${rows}${change}</tbody></table>`;
}

function renderSignature(e: A4SignatureElement, model: A4RenderModel): string {
  const line = `<div class="sig-line"></div>`;
  const pos = e.linePosition ?? 'above';
  const labels = bilingual(e.labelAr, e.labelEn, Boolean(model.arabicLabels));
  return `<div class="sig">${pos === 'above' ? line : ''}${labels}${pos === 'below' ? line : ''}</div>`;
}

function renderElementInner(e: A4Element, model: A4RenderModel): string {
  switch (e.type) {
    case 'text':
      return `<div class="txt">${applyTokens(e.text, model.fields)}</div>`;
    case 'field':
      return renderField(e, model);
    case 'logo': {
      const src = (e.source ?? 'receipt') === 'menu' ? model.menuLogoDataUrl : model.logoDataUrl;
      if (!src) return '';
      return `<img src="${esc(src)}" alt="" style="width:100%;height:100%;object-fit:${e.fit ?? 'contain'}" />`;
    }
    case 'image':
      return e.dataUrl ? `<img src="${esc(e.dataUrl)}" alt="" style="width:100%;height:100%;object-fit:${e.fit ?? 'contain'}" />` : '';
    case 'line': {
      const t = e.thicknessPx ?? 1;
      const ls = e.lineStyle ?? 'solid';
      const c = e.color ?? '#000';
      return (e.orientation ?? 'h') === 'v'
        ? `<div style="width:0;height:100%;border-left:${t}px ${ls} ${c}"></div>`
        : `<div style="height:0;width:100%;border-top:${t}px ${ls} ${c};margin-top:${mm(Math.max(0, e.h / 2))}"></div>`;
    }
    case 'rect':
      return '';
    case 'grid':
      return renderGrid(e, model);
    case 'qr':
      return model.qrSvg ? `<div class="qr">${model.qrSvg}</div>` : '';
    case 'barcode':
      if (!model.barcodeSvg) return '';
      return `<div class="barcode">${e.stretch ? model.barcodeSvg.replace('<svg ', '<svg preserveAspectRatio="none" ') : model.barcodeSvg}</div>`;
    case 'signature':
      return renderSignature(e, model);
    case 'items':
      return renderItems(e, model);
    case 'totals':
      return renderTotals(e, model);
    case 'payments':
      return renderPayments(e, model);
    default:
      return '';
  }
}

function elementVisible(e: A4Element, model: A4RenderModel): boolean {
  if (e.hideIfEmpty && tokenEmpty(e.hideIfEmpty, model.fields)) return false;
  if (e.type === 'field' && e.hideIfEmpty === undefined) return true;
  return true;
}

const DEFAULT_FONT_STACK = 'Tahoma, "Geeza Pro", "Noto Sans Arabic", Arial, sans-serif';

/**
 * Render a full HTML document for a custom A4 design.
 *
 * Page-anchored elements are absolutely positioned on the sheet. The items
 * table and every 'after-items' element live in a flow container placed at
 * the table's top edge, so the table may grow (and paginate) and the footer
 * elements follow its bottom edge.
 */
export function renderA4DesignHtml(design: A4Design, model: A4RenderModel): string {
  const page = design.page ?? { widthMm: 210, heightMm: 297 };
  const W = page.widthMm || 210;
  const H = page.heightMm || 297;
  const fontFamily = page.fontFamily || DEFAULT_FONT_STACK;
  const basePt = page.baseFontPt ?? 10;
  const color = page.color || '#000';
  const dir = page.direction === 'auto' || !page.direction ? model.direction : page.direction;

  const elements = (design.elements ?? []).filter((e) => elementVisible(e, model));
  const items = elements.find((e): e is A4ItemsElement => e.type === 'items') ?? null;
  const pageEls = elements.filter((e) => e !== items && (e.anchor ?? 'page') === 'page');
  const afterEls = elements.filter((e) => e !== items && e.anchor === 'after-items');

  const pageHtml = pageEls.map((e) => elementHtml(e, model)).join('\n');

  let flowHtml = '';
  if (items) {
    const afterHeight = afterEls.reduce((m, e) => Math.max(m, e.y + e.h), 0);
    const afterHtml = afterEls.map((e) => elementHtml(e, model)).join('\n');
    flowHtml =
      `<div class="flow" style="position:absolute;left:0;top:${mm(items.y)};width:${mm(W)}">` +
      // `margin-right:auto` is load-bearing on an RTL page. The items table is
      // the one element placed by MARGIN rather than by `left`, and a block
      // with both margins set is over-constrained: the browser then discards
      // whichever margin is on the trailing side — `margin-right` in LTR, but
      // `margin-left` in RTL. Without the auto, an RTL sheet ignored the `x`
      // entirely and snapped the table flush against the page's right edge,
      // out of line with every other element on the sheet. With it, the auto
      // margin absorbs the remainder and `x` is honoured in both directions.
      `<div class="items-wrap" style="margin-left:${mm(items.x)};margin-right:auto;width:${mm(items.w)};min-height:${mm(items.h)};${textCss(items.style)};${frameCss(items.frame)}">${renderItems(items, model)}</div>` +
      (afterEls.length > 0 ? `<div class="after" style="position:relative;height:${mm(afterHeight)}">${afterHtml}</div>` : '') +
      `</div>`;
  } else if (afterEls.length > 0) {
    // No items table to anchor to: treat the offsets as page coordinates.
    flowHtml = afterEls.map((e) => elementHtml(e, model)).join('\n');
  }

  // Rules for the features added in 2026-09 (watermark, rotation, fixed-height
  // totals rows) are emitted only when the design uses them, so every design
  // saved before them renders byte for byte as it did.
  const all = design.elements ?? [];
  const extraCss = [
    all.some((e) => e.type === 'totals' && e.rows.some((r) => r.heightMm))
      ? '  table.totals tr.fixed-h td { padding-top: 0; padding-bottom: 0; }\n' : '',
    all.some((e) => e.rotate) ? '  .rot { position: absolute; left: 50%; top: 50%; }\n' : '',
    page.watermark?.dataUrl
      ? '  .wm { position: absolute; pointer-events: none; }\n  .wm img { width: 100%; height: 100%; object-fit: contain; display: block; }\n' : '',
  ].join('');

  const css = `
  @page { size: ${W}mm ${H}mm; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; color: ${color}; font-family: ${fontFamily}; font-size: ${basePt}pt; line-height: 1.3; }
  .sheet { position: relative; width: ${mm(W)}; min-height: ${mm(H)}; background: ${page.background || '#fff'}; overflow: visible; }
  .el { overflow: hidden; }
  .el-items, .el-totals, .el-payments, .el-grid { overflow: visible; }
  .txt { white-space: pre-wrap; word-break: break-word; }
  .ar { unicode-bidi: plaintext; }
  .field { display: flex; align-items: center; gap: 2mm; height: 100%; min-width: 0; }
  .field:has(> .fl-box) { gap: 0; }
  .field-stacked { flex-direction: column; align-items: stretch; gap: 0.5mm; }
  .fl { white-space: nowrap; }
  .fl-box { align-self: stretch; display: flex; align-items: center; }
  .fl-lines { display: flex; flex-direction: column; line-height: 1.15; }
  .fl-end { text-align: end; margin-inline-start: auto; }
  .fv { flex: 1 1 auto; min-width: 0; display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-height: 1.4em; unicode-bidi: plaintext; }
  .fv-wrap { white-space: normal; text-overflow: clip; overflow-wrap: break-word; }
  table.a4grid { width: 100%; border-collapse: collapse; table-layout: fixed; }
  table.a4grid td { padding: 0.8mm 1.5mm; vertical-align: middle; unicode-bidi: plaintext; }
  table.items { width: 100%; border-collapse: collapse; }
  table.items th, table.items td { padding: 1mm 1.5mm; vertical-align: middle; }
  table.items thead { display: table-header-group; }
  table.items tbody.it { break-inside: avoid; page-break-inside: avoid; }
  table.items td.sub { font-size: 0.85em; color: #333; }
  .it-ar { font-size: 0.95em; }
  .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  table.totals, table.pay { width: 100%; border-collapse: collapse; }
  table.totals td, table.pay td { padding: 1mm 1.5mm; vertical-align: middle; }
${extraCss}  .pay-title { font-weight: 700; margin-bottom: 1mm; }
  .sig { display: flex; flex-direction: column; justify-content: flex-end; height: 100%; text-align: center; }
  .sig-line { border-top: 1px solid currentColor; margin: 0 0 1.5mm; }
  .qr, .qr svg { width: 100%; height: 100%; }
  .barcode, .barcode svg { width: 100%; height: 100%; }
  tr.ret td { color: #7a1c1c; }
  `;

  return `<!DOCTYPE html>
<html lang="${dir === 'rtl' ? 'ar' : 'en'}" dir="${dir}">
<head>
<meta charset="utf-8">
<title>Invoice</title>
<style>${css}</style>
</head>
<body>
<div class="sheet">
${watermarkHtml(page, W, H)}${pageHtml}
${flowHtml}
</div>
</body>
</html>`;
}

// ── Sample data (admin live preview + tests) ───────────────────────────────

export function sampleA4RenderModel(overrides: Partial<A4RenderModel> = {}): A4RenderModel {
  const fields: Record<string, string> = {};
  for (const t of A4_FIELD_TOKENS) fields[t.token] = t.sample;
  return {
    fields,
    items: [
      { code: '1001', name: 'Super Chlor Extra 1 L', nameAr: 'سوبر كلور اكسترا 1 لتر', unit: 'Piece', qty: 1, unitPrice: 15, discount: 0, net: 15, tax: 0, gross: 15, total: 15 },
      { code: '1002', name: 'Garden hose 20 m', nameAr: 'خرطوم حديقة 20 متر', unit: 'Piece', qty: 2, unitPrice: 4.25, discount: 0, net: 8.5, tax: 0, gross: 8.5, total: 8.5 },
    ],
    totals: { subtotal: 23.5, discount: 0, delivery: 0, afterDiscount: 23.5, tax: 0, total: 23.5, paid: 23.5, change: 0 },
    totalInWords: { en: 'Twenty-three Kuwaiti Dinars and five hundred fils only', ar: 'ثلاثة وعشرون دينار كويتي وخمسمائة فلس فقط لا غير' },
    currency: { code: 'KWD', precision: 3 },
    payments: [{ label: 'Cash', amount: 23.5 }],
    logoDataUrl: null,
    menuLogoDataUrl: null,
    qrSvg: null,
    direction: 'ltr',
    ...overrides,
  };
}
